import { useEffect, useState } from 'react';
import { api } from '../api.js';

export default function AiScheduler({ onBack, onGoToRoster }) {
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [days, setDays] = useState(7);
  const [filterShift, setFilterShift] = useState('all');
  const [filterDistrict, setFilterDistrict] = useState('all');

  async function loadSchedule() {
    setLoading(true);
    setErr('');
    setStatusMsg('');
    try {
      const res = await api.getAutoSchedule(days);
      setData(res);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSchedule();
  }, [days]);

  async function handleCommit() {
    if (!confirm(`Are you sure you want to commit these ${data?.totalSlots || 0} AI-slotted visits directly into the official clinical roster and nursing visits database?`)) {
      return;
    }
    setCommitting(true);
    setStatusMsg('');
    try {
      const res = await api.commitAutoSchedule(days);
      setStatusMsg(`✓ Successfully slotted and committed ${res.committedCount} visits to the clinical roster & D1 database!`);
      if (onGoToRoster) {
        setTimeout(onGoToRoster, 1600);
      }
    } catch (e) {
      setErr('Commit failed: ' + e.message);
    } finally {
      setCommitting(false);
    }
  }

  const slots = data?.schedule || [];
  let filtered = slots;
  if (filterShift !== 'all') filtered = filtered.filter((s) => s.shift_type === filterShift);
  if (filterDistrict !== 'all') filtered = filtered.filter((s) => s.district === filterDistrict);

  const districts = ['all', ...new Set(slots.map((s) => s.district))];

  return (
    <div className="card" style={{ maxWidth: 1140, margin: '0 auto' }}>
      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onBack && <button className="link" onClick={onBack}>← Back</button>}
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>🤖</span> AI Care Auto-Scheduler
            </h2>
            <span className="cbadge proc" style={{ background: 'linear-gradient(135deg, #0284c7, #0369a1)', color: '#fff' }}>
              ⚡ "Slots Out Everything"
            </span>
          </div>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Intelligent shift dispatcher matching patient care plans, acuity, nurse competencies, valid APCs &amp; Penang travel clusters.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ padding: '7px 12px', borderRadius: 8, background: '#0a1929', color: '#fff', border: '1px solid rgba(56, 189, 248, 0.3)' }}
          >
            <option value={3}>Next 3 Days</option>
            <option value={7}>Next 7 Days (1 Week)</option>
            <option value={14}>Next 14 Days (Fortnight)</option>
            <option value={30}>Next 30 Days (Full Month)</option>
          </select>
          <button className="ghost sm" onClick={loadSchedule} disabled={loading}>
            {loading ? 'Analyzing…' : '🔄 Recalculate Slots'}
          </button>
          <button className="pri sm" onClick={handleCommit} disabled={committing || loading || slots.length === 0}>
            {committing ? 'Committing…' : '⚡ Approve & Dispatch Roster'}
          </button>
        </div>
      </div>

      {statusMsg && (
        <div style={{ background: 'rgba(5, 150, 105, 0.15)', border: '1px solid rgba(5, 150, 105, 0.4)', borderRadius: 8, padding: '12px 16px', color: '#34d399', fontWeight: 600, marginBottom: 16 }}>
          {statusMsg}
        </div>
      )}

      {err && (
        <div style={{ background: 'rgba(220, 38, 38, 0.15)', border: '1px solid rgba(220, 38, 38, 0.4)', borderRadius: 8, padding: '12px 16px', color: '#f87171', fontWeight: 600, marginBottom: 16 }}>
          {err}
        </div>
      )}

      {/* KPI Overview Strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div style={{ background: '#040d1a', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Auto-Slotted Visits</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#38bdf8' }}>{data?.totalSlots || 0} <span style={{ fontSize: '0.85rem', fontWeight: 400 }}>slots</span></div>
        </div>
        <div style={{ background: '#040d1a', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Active Patients Covered</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#10b981' }}>{data?.patientPoolCount || 0} <span style={{ fontSize: '0.85rem', fontWeight: 400 }}>patients</span></div>
        </div>
        <div style={{ background: '#040d1a', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>Qualified Nurses Slotted</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#f59e0b' }}>{data?.activeNursesCount || 0} <span style={{ fontSize: '0.85rem', fontWeight: 400 }}>nurses</span></div>
        </div>
        <div style={{ background: '#040d1a', border: '1px solid rgba(5, 150, 105, 0.3)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ color: '#34d399', fontSize: '0.75rem', textTransform: 'uppercase' }}>Workforce Governance</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#10b981', marginTop: 4 }}>100% Valid APC Verified</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <div className="tabs" style={{ margin: 0 }}>
          {[
            { k: 'all', l: 'All Shifts' },
            { k: 'AM', l: '☀️ AM (08:00 – 15:00)' },
            { k: 'PM', l: '🌤️ PM (15:00 – 22:00)' },
            { k: 'NIGHT', l: '🌙 Night N (22:00 – 08:00)' },
            { k: 'one_time', l: '⏱️ One-Time (Family Preference)' }
          ].map((s) => (
            <button
              key={s.k}
              className={'tab' + (filterShift === s.k ? ' on' : '')}
              onClick={() => setFilterShift(s.k)}
            >
              {s.l}
            </button>
          ))}
        </div>

        <div>
          <select
            value={filterDistrict}
            onChange={(e) => setFilterDistrict(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 6, background: '#0a1929', color: '#fff', border: '1px solid rgba(56, 189, 248, 0.3)' }}
          >
            {districts.map((d) => (
              <option key={d} value={d}>
                District: {d === 'all' ? 'All Penang Areas' : d.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Slots List */}
      {loading ? (
        <p className="muted">AI is analyzing patient care needs and calculating route clusters…</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
          <p>No slots found matching the selected filters.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map((s) => (
            <div
              key={s.slot_id}
              style={{
                background: '#040d1a',
                border: '1px solid rgba(56, 189, 248, 0.2)',
                borderRadius: 8,
                padding: '14px 18px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 260 }}>
                <div style={{ background: 'rgba(56, 189, 248, 0.1)', padding: '8px 12px', borderRadius: 8, textAlign: 'center', minWidth: 80 }}>
                  <div style={{ fontSize: '0.72rem', color: '#38bdf8', fontWeight: 700 }}>{s.day_label}</div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#f8fafc', marginTop: 2 }}>{s.date.slice(5)}</div>
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 800, fontSize: '1.05rem', color: '#fff' }}>{s.patient_name}</span>
                    <span className="cbadge lt" style={{ fontSize: '0.72rem' }}>{s.shift_window}</span>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#94a3b8', marginTop: 4 }}>
                    Care Need: <strong style={{ color: '#cbd5e1' }}>{s.care_type}</strong> · Area: <span style={{ color: '#38bdf8' }}>{s.district.replace(/_/g, ' ')}</span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                    📍 {s.patient_address}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Assigned Nurse:</div>
                  <div style={{ fontWeight: 700, color: '#38bdf8', fontSize: '0.95rem' }}>{s.nurse_name}</div>
                  <div style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 600 }}>{s.apc_status}</div>
                </div>
                <div style={{ background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#34d399', padding: '6px 10px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 700 }}>
                  {s.competency_match}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer Info */}
      <div style={{ marginTop: 24, padding: 14, background: '#020b14', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', fontSize: '0.82rem', color: '#64748b' }}>
        <strong>AI Dispatcher Principles:</strong>
        <p style={{ margin: '4px 0 0', lineHeight: 1.6 }}>
          1. Zero cross-bridge travel where possible (Island visits assigned to Island-based nurses; Seberang Perai assigned to Mainland nurses).<br />
          2. Mandatory competency verification for complex procedures (ANTT wound dressing, tracheostomy, PEG tube replacement).<br />
          3. Enforces Malaysian Nursing Board (LJM) continuous shift rest intervals and valid APC renewal verification.
        </p>
      </div>
    </div>
  );
}
